import clsx from 'clsx';
import { launchMenuOptions, useDevice } from '../../hooks/Devices/useDevice';
import { useCitrusView } from '../../contexts/CitrusViewContext';
import { ReactNode, useState } from 'react';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useOperator } from '../../contexts/OperatorContext';
import { useUser } from '../../contexts/UserContext';
import { InfoOutlined } from '@material-ui/icons';
import { Tooltip } from '../../components/Tooltip';

type Props = {
  device: Device;
  onClose(): void;
};

export const ActionOptions = ({ device, onClose }: Props) => {
  const operator = useOperator();
  const {
    isMobile,
    connectedUser,
    reboot: rebootEncoder,
    rebooting: encoderRebooting,
    resetSource,
  } = useCitrusView();
  const { reboot, remoteDRA, launchSettingsApplication } = useDevice(
    'QASTN',
    device
  );
  const { email } = useUser();
  const disabled = connectedUser !== email || encoderRebooting;

  const handleReboot = () => {
    reboot();
    onClose();
  };

  const handleDRA = () => {
    remoteDRA();
    onClose();
  };

  const handleLaunch = (id: string) => {
    launchSettingsApplication(id);
    onClose();
  };

  return (
    <div className="flex flex-col overflow-y-auto overflow-x-hidden whitespace-nowrap max-h-[calc(100%-135px)]">
      <ul className={clsx({ 'space-y-4 px-4': isMobile })}>
        <li className="flex items-center justify-between px-4 py-2">
          <button className="tg-border-button text-xs" onClick={resetSource}>
            Reset Source
          </button>
          <Tooltip
            title="Usefull for clearing up unexpected video loops."
            showArrow
          >
            <InfoOutlined fontSize="small" />
          </Tooltip>
        </li>
        <li className="sticky top-0 text-lg border-b border-border-dark py-2 px-2 bg-black z-10">
          Encoder
        </li>
        <li>
          <ActionButton onClick={rebootEncoder} disabled={disabled}>
            {encoderRebooting ? 'Rebooting Encoder...' : 'Reboot Encoder'}
          </ActionButton>
        </li>
        <li className="sticky top-0 text-lg border-b border-border-dark py-2 px-2 bg-black z-10">
          STB
        </li>
        <ConfirmReboot onConfirm={handleReboot} disabled={disabled} />
        {operator.type === 'Residential' && (
          <ConfirmDRA onConfirm={handleDRA} disabled={disabled} />
        )}
        {operator.type === 'Residential' && (
          <>
            <li className="sticky top-0 text-lg border-b border-border-dark px-2 py-2 bg-black z-10">
              Menus & Settings
            </li>
            {launchMenuOptions.map((action, index) => (
              <li key={`action-${index + 1}`} className="w-full">
                <ActionButton
                  onClick={() => handleLaunch(action.id)}
                  disabled={disabled}
                >
                  {action.name}
                </ActionButton>
              </li>
            ))}
          </>
        )}
        <li className="text-lg border-b border-border-dark px-2 py-2 mt-auto bg-black">
          Your Macros{' '}
          <span className="text-primary text-xs italic">(Coming Soon)</span>
        </li>
        <li className="w-full py-2 px-4">None Configured</li>
      </ul>
    </div>
  );
};

type ConfirmActionProps = {
  disabled?: boolean;
  onConfirm(): void;
};

type ActionButtonProps = {
  children: ReactNode;
  disabled?: boolean;
  onClick(): void;
};

const ActionButton = ({
  children,
  disabled = false,
  onClick,
}: ActionButtonProps) => {
  const { isMobile } = useCitrusView();

  return (
    <button
      className={clsx(
        'w-full py-2 px-4 text-left hover:bg-primary/30 disabled:opacity-30 disabled:pointer-events-none',
        { 'text-base border border-white rounded': isMobile }
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const ConfirmReboot = ({ disabled = false, onConfirm }: ConfirmActionProps) => {
  const [open, setOpen] = useState(false);

  return (
    <li>
      <ActionButton onClick={() => setOpen(true)} disabled={disabled}>
        Reboot STB
      </ActionButton>
      <ConfirmModal
        header="Reboot"
        description="Are you sure you want to reboot the STB?"
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={onConfirm}
        confirmText="Reboot"
        cancelText="Cancel"
      />
    </li>
  );
};

const ConfirmDRA = ({ disabled, onConfirm }: ConfirmActionProps) => {
  const [open, setOpen] = useState(false);

  return (
    <li>
      <ActionButton onClick={() => setOpen(true)} disabled={disabled}>
        DRA STB
      </ActionButton>
      <ConfirmModal
        header="DRA"
        description="Are you sure you want to DRA the STB?"
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={onConfirm}
        confirmText="DRA"
        cancelText="Cancel"
      />
    </li>
  );
};
